import { Box, Flex, IconButton, Tooltip, Icon } from "@chakra-ui/react";
import { useState, useCallback, useRef, useEffect } from "react";
import { Applications } from "./Applications";
import { ChatPanel } from "../../components/ChatDrawer/chat";
import { UserService } from "../../services/UserService"; // Adjust import path as needed
import { WelcomeModal } from "../../components/WelcomeModal/WelcomeModal";
import { getAxiosClient } from "../../services/HttpsService";
import { BsStars, BsX } from "react-icons/bs";

export const ApplicationsLayout = () => {
  const [leftWidth, setLeftWidth] = useState(75);
  const [isDragging, setIsDragging] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isCheckingRole, setIsCheckingRole] = useState(true);
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const token = UserService.getParsedToken();

  const roles =
    UserService.getParsedToken()?.resource_access?.["analytics-service"]?.roles;

  useEffect(() => {
    const checkUserRole = async () => {
      if (!UserService.isLoggedIn()) {
        UserService.doLogin();
        return;
      }
      try {
        await UserService.updateToken(() => {});
      } catch (error) {
        console.error("Failed to update token on load:", error);
      }

      if (roles && roles.length !== 0) {
        setShowWelcomeModal(false);
      } else {
        setShowWelcomeModal(true);
      }

      setIsCheckingRole(false);
    };

    checkUserRole();
  }, []);

  const handleStartExploring = async () => {
    try {
      await addUser();
      window.location.reload();
    } catch (error) {
      console.error("Failed to assign user role:", error);
    }
  };

  const addUser = async () => {
    const userProfile = UserService.getProfile();
    if (!userProfile) {
      throw new Error("User profile not found.");
    }

    const newUserPayload = {
      userTyp: "User",
      userIdx: UserService.getUsername(),
      userPwd: null,
      userFirstNm: userProfile.givenName,
      userLastNm: userProfile.familyName,
      userEmail: userProfile.email,
      userPh: userProfile.phone,
      userMgr: null,
      localeCd: "en_US",
      timezoneCd: "UTC",
      isActive: "Y",
      createdBy: "system",
    };

    const url = "/api/eza_sys_user";
    const response = await getAxiosClient().post(url, newUserPayload);

    if (response.status !== 201) {
      throw new Error("Failed to add user");
    }
    return response.data;
  };

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      const newLeftWidth = (mouseX / containerRect.width) * 100;
      const constrainedWidth = Math.min(Math.max(newLeftWidth, 20), 80);
      setLeftWidth(constrainedWidth);
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (isCheckingRole) {
    return (
      <Flex h="90vh" w="100%" align="center" justify="center">
        <Box>Checking user permissions...</Box>
      </Flex>
    );
  }

  return (
    <>
      <Flex
        ref={containerRef}
        h="88.5vh"
        w="100%"
        overflow="hidden"
        position="relative"
      >
        {/* Left Panel - Applications */}
        <Box
          width={isAiAssistantOpen ? `${leftWidth}%` : "100%"}
          overflowY="auto"
          bg="transparent"
          pr={isAiAssistantOpen ? 2 : 4}
          pl={4}
          pb={4}
          transition={isDragging ? "none" : "width 0.3s ease"}
          position="relative"
          zIndex="1"
          sx={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none',
          }}
        >
          <Applications />
        </Box>

        {/* Invisible Resizable Divider - Only functional when AI Assistant is open */}
        {isAiAssistantOpen && (
          <Box
            width="4px"
            bg="transparent"
            cursor="col-resize"
            position="relative"
            onMouseDown={handleMouseDown}
            _hover={{
              bg: "transparent",
            }}
            _active={{
              bg: "transparent",
            }}
          />
        )}

        {/* Right Panel - Chat */}
        <Box
          flex={isAiAssistantOpen ? "1" : "0"}
          display="flex"
          minWidth={isAiAssistantOpen ? "300px" : "0"}
          maxWidth={isAiAssistantOpen ? "none" : "0"}
          overflow="hidden"
          transition="all 0.3s ease"
          pb={isAiAssistantOpen ? 4 : 0}
          pl={isAiAssistantOpen ? 2 : 0}
          pr={isAiAssistantOpen ? 4 : 0}
        >
          {isAiAssistantOpen && <ChatPanel />}
        </Box>

        {/* AI Assistant Toggle Button */}
        <Box position="absolute" top="2" right="2" zIndex="10">
          <Tooltip
            label={
              isAiAssistantOpen ? "Hide AI Assistant" : "Show AI Assistant"
            }
            placement="left"
          >
            <IconButton
              aria-label={
                isAiAssistantOpen ? "Hide AI Assistant" : "Show AI Assistant"
              }
              icon={
                <Icon
                  as={
                    isAiAssistantOpen
                      ? (BsX as React.ElementType)
                      : (BsStars as React.ElementType)
                  }
                  boxSize={4}
                />
              }
              onClick={() => setIsAiAssistantOpen(!isAiAssistantOpen)}
              bg="rgba(118, 82, 247, 0.8)"
              color="white"
              size="sm"
              borderRadius="full"
              _hover={{
                bg: "rgba(118, 82, 247, 1)",
                transform: "scale(1.1)",
              }}
              _active={{
                bg: "rgba(118, 82, 247, 0.9)",
              }}
              transition="all 0.2s ease"
            />
          </Tooltip>
        </Box>
      </Flex>

      {/* Welcome Modal */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onStartExploring={handleStartExploring}
      />
    </>
  );
};
